import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function PdfMerge() {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function onMerge() {
    if (files.length < 2) {
      toast.error("Select at least 2 PDFs");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/pdf/merge", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-merge-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_merge")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_merge_desc")}</p>

      <div className="mt-10">
        <DropZone
          multiple
          accept="application/pdf"
          onFiles={(fs) => setFiles((prev) => [...prev, ...fs.filter((f) => f.name.toLowerCase().endsWith(".pdf"))])}
          testId="merge-dropzone"
        />

        {files.length > 0 && (
          <ul className="mt-6 grid gap-2" data-testid="merge-file-list">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3 bg-white">
                <span className="text-sm font-medium truncate">{i + 1}. {f.name}</span>
                <button
                  className="text-xs text-zinc-500 hover:text-black"
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  data-testid={`merge-remove-${i}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex gap-3">
          <Button
            onClick={onMerge}
            disabled={busy || files.length < 2}
            className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11"
            data-testid="merge-submit"
          >
            {busy ? t("processing") : `${t("cta_apply")} →`}
          </Button>
          {files.length > 0 && (
            <Button variant="ghost" className="rounded-sm h-11" onClick={() => setFiles([])} data-testid="merge-clear">
              {t("cta_clear")}
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="merge-result">
            <span className="eyebrow">{t("success")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <div className="mt-4">
              <Button
                onClick={() => downloadFile(result.id, result.original_filename)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10"
                data-testid="merge-download"
              >
                ↓ {t("cta_download")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
